import React, { useRef, useState } from "react";
import type { UploadedFileMeta } from "../../features/onboarding/types";

type ValidatedUploadFieldProps = {
  value: UploadedFileMeta[];
  onChange: (value: UploadedFileMeta[]) => void;
  accept: string[];
  multiple?: boolean;
  helperText?: string;
};

const bytesToLabel = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ValidatedUploadField({
  value,
  onChange,
  accept,
  multiple = true,
  helperText,
}: ValidatedUploadFieldProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const acceptString = accept.join(",");

  const appendFiles = (files: FileList | File[]) => {
    Array.from(files).forEach((file) => {
      if (!accept.includes(file.type)) {
        const rejected: UploadedFileMeta = {
          id: `${file.name}-${file.lastModified}`,
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 100,
          status: "error",
          error: "Unsupported file type.",
        };
        onChange([...(multiple ? value : []), rejected]);
        return;
      }

      const uploadId = `${file.name}-${file.lastModified}`;
      const initial: UploadedFileMeta = {
        id: uploadId,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: "uploading",
      };

      const nextValue = multiple ? [...value, initial] : [initial];
      onChange(nextValue);

      let progress = 0;
      const timer = window.setInterval(() => {
        progress += 25;
        const finalStatus = progress >= 100 ? "uploaded" : "uploading";
        onChange(
          (multiple ? nextValue : [initial]).map((item) =>
            item.id === uploadId
              ? { ...item, progress: Math.min(progress, 100), status: finalStatus }
              : item
          )
        );

        if (progress >= 100) {
          window.clearInterval(timer);
        }
      }, 120);
    });
  };

  const removeFile = (id: string) => {
    onChange(value.filter((item) => item.id !== id));
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          appendFiles(event.dataTransfer.files);
        }}
        className={`flex min-h-[140px] w-full flex-col items-center justify-center rounded-3xl border border-dashed px-5 py-6 text-center transition ${
          dragging ? "border-[#93C893] bg-[#EEF9EE]" : "border-[#CBD5E1] bg-[#F8FAFC]"
        }`}
      >
        <span className="material-icons mb-3 rounded-full bg-white p-3 text-[24px] text-[#166534] shadow-sm">
          upload
        </span>
        <span className="text-base font-semibold text-[#0F172A]">
          Drop files here or tap to upload
        </span>
        <span className="mt-1 text-sm text-[#6B7280]">
          {helperText ?? "PDF, DOCX, and PPTX supported"}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={acceptString}
        className="hidden"
        onChange={(event) => {
          if (event.target.files?.length) {
            appendFiles(event.target.files);
          }
          event.target.value = "";
        }}
      />

      {value.length > 0 && (
        <div className="mt-4 space-y-3">
          {value.map((file) => (
            <div
              key={file.id}
              className="rounded-2xl border border-[#E5E7EB] bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-[#0F172A]">
                    {file.name}
                  </div>
                  <div className="mt-1 text-xs text-[#6B7280]">
                    {bytesToLabel(file.size)}
                  </div>
                  {file.error && <div className="mt-1 text-xs text-red-600">{file.error}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E7EB]"
                >
                  <span className="material-icons text-[18px]">close</span>
                </button>
              </div>
              <div className="mt-3 h-2 rounded-full bg-[#E5E7EB]">
                <div
                  className={`h-2 rounded-full ${
                    file.status === "error" ? "bg-red-500" : "bg-[#93C893]"
                  }`}
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
